import { useGetAllVoicesFlat } from '@/pages/agents/hooks';
import { useAllAvatars } from '@/pages/avatar';
import { useAgentEditorStore } from '@/state/root';
import { AgentCreateTabType, Presenter, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { AgentAppearance } from './AgentAppearance';
import { AgentAvatar } from './AgentAvatar';
import { AgentDetails } from './AgentDetails';
import { AgentKnowledgeBase } from './AgentKnowledgeBase';
import { ChatSettings } from './ChatSettings';

const PaddingContainer = styled.div<{ isAnonymous?: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ isAnonymous }) =>
        isAnonymous
            ? `
    margin: 6rem 0 2rem 0;
    overflow-y: auto;
    `
            : `
    padding: 40px 156px 80px 156px;
    width: 840px;
    height: fit-content;
    `}
`;

const AgentAppearanceSection = styled.div<{ isAnonymous?: boolean }>`
    ${({ isAnonymous }) => isAnonymous && 'overflow: hidden;'}
    display: grid;
    gap: 2rem;
    margin: 0;
`;

interface Props {
    presenter?: Presenter;
    isAnonymous?: boolean;
    agentId: string;
}

export const AgentTabs = observer(({ presenter, isAnonymous, agentId }: Props) => {
    const agentEditorStore = useAgentEditorStore();
    const allVoices = useGetAllVoicesFlat();

    const { isLoading } = useAllAvatars(PresenterUsecase.Agent);

    const [showMissingAvatar, setShowMissingAvatar] = useState(false);

    useEffect(() => {
        if (!agentEditorStore.isEditorLoading) {
            setShowMissingAvatar(!(presenter || isLoading));
        }
    }, [presenter, isLoading, agentEditorStore.isEditorLoading]);

    const isVoiceExist = useMemo(
        () =>
            !!allVoices.find(voice => voice.id === agentEditorStore.ttsSelectedVoice?.id) ||
            agentEditorStore.ttsSelectedVoice?.voiceType === 'Injected',
        [allVoices, agentEditorStore.ttsSelectedVoice]
    );

    const missingLanguage = agentEditorStore.ttsSelectedVoice && !agentEditorStore.ttsSelectedLanguage;

    return (
        <PaddingContainer isAnonymous={isAnonymous}>
            {agentEditorStore.currentTab === AgentCreateTabType.Appearance && (
                <>
                    {!isAnonymous ? (
                        <>
                            {showMissingAvatar && (
                                <ErrorBanner
                                    title="Missing avatar"
                                    description="Your agent is missing an avatar, select or upload a new one to allow sound and face animations."
                                />
                            )}
                            <AgentAppearanceSection isAnonymous={isAnonymous} style={{ margin: 0 }}>
                                <Typography variant="h2">Appearance</Typography>
                                <AgentAppearance agentId={agentId} presenter={presenter} hideOverflowY={true} />
                            </AgentAppearanceSection>
                        </>
                    ) : (
                        <AgentAppearanceSection isAnonymous={isAnonymous} style={{ margin: '6rem 22% 0 22%' }}>
                            <AgentAvatar enableStitchSelection={presenter?.type === VideoType.TALK} />
                            <AgentAppearance agentId={agentId} presenter={presenter} hideOverflowY={false} />
                        </AgentAppearanceSection>
                    )}
                </>
            )}
            {agentEditorStore.currentTab === AgentCreateTabType.Details && (
                <>
                    {!isVoiceExist && (
                        <ErrorBanner
                            title="Missing voice"
                            description="Your agent is missing a voice, select or upload a new one to allow sound and face animations."
                        />
                    )}
                    {missingLanguage && <ErrorBanner title="Missing language" description="Your agent is missing a language." />}
                    <AgentDetails />
                </>
            )}
            {agentEditorStore.currentTab === AgentCreateTabType.KnowledgeBase && <AgentKnowledgeBase />}
            {agentEditorStore.currentTab === AgentCreateTabType.ChatSettings && <ChatSettings />}
        </PaddingContainer>
    );
});
